import { createDepotConfiguration, createConnection, createServer } from './TransportUtils';
import {
  ReportDataLevel,
  Connection,
  Depot,
  Server,
  TransportWarning,
  DepotEvent,
} from '@sas-dvr/ltjs-transport';
import { TransportVoucher } from './TransportVoucher';
import { SASReport } from './SASReport';
import { ltjsProperty, ltjsDestructor } from '../ltjs-decorators';
import { runWhenInitialized, whenInitialized } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { getServerForConnection, reloadAllReports } from './SASReportManager';
import { embeddedBundle } from '@sas-dvr/internal-va-react-core/locale/embeddedI18N';
import { deleteReportImageManager } from '@sas-dvr/internal-va-react-core/components/ReportImageManager';
import { validUrl } from '@sas-dvr/internal-va-react-core/components/utils/validURL';
import {
  getConnection,
  deauthorize,
  AuthenticationType,
  getBypassAuthorizationCheck,
} from '@sas-dvr/internal-va-react-core/utils/authorization';
import { ReplaySubject, Observable, Subscription, combineLatest, asyncScheduler } from 'rxjs';
import { takeUntil, distinctUntilChanged, skip, debounceTime } from 'rxjs/operators';
import { IServer } from './internal/IServer';
import {
  hasHighContrastReportTheme$,
  getUseHighContrastReportTheme,
} from '@sas-dvr/internal-va-react-core/config';
import { locale$, formatterLocale$ } from '@sas-dvr/internal-va-react-core/config';
import {
  ConnectionStatus,
  ServerType,
} from '@sas-dvr/internal-va-react-core/store/transport/transportState';
import { registerServerFonts } from './internal/fonts/serverFonts';

const genericErrorMessage = embeddedBundle['SASServer.genericError.msg']();

let depot: Depot | null = null;
let depotSubscription: Subscription | null = null;

runWhenInitialized(() => {
  const depot = getDepot();
  depot.attachEvent(Depot.DEPOT_CONNECTION_REQUIRES_AUTHENTICATION, (e) => {
    if (getBypassAuthorizationCheck()) {
      console.warn('Authentication needed while in viya app mode. This should not happen.');
      return;
    }
    if (e instanceof DepotEvent) {
      const model = e.getAssociatedModel();
      if (!(model instanceof Connection)) {
        return;
      }
      const server = getServerForConnection(model);
      if (!(server instanceof SASServer)) {
        return;
      }
      server.deauthorize();
    }
  });
});

function processWarning(warning: TransportWarning | null) {
  return warning ? warning.getMessage() || genericErrorMessage : genericErrorMessage;
}

function createDepot() {
  return new Depot(
    createDepotConfiguration({
      dataLevel: ReportDataLevel.NODATA,
      highContrast: getUseHighContrastReportTheme(),
    })
  );
}

export interface SASServerOptions {
  url: string;
  description?: string | null;
  authenticationType: AuthenticationType;
}

export function getDepot() {
  if (!depot) {
    depot = createDepot().retain();

    const localDepot = depot;
    depotSubscription = hasHighContrastReportTheme$.subscribe((value) => {
      // Update the Depot and reload SASReports when the high contrast setting changes
      if (localDepot.getDepotConfiguration()?.getUseHighContrastReportStyle() !== value) {
        localDepot.setUseHighContrastReportStyle(value);
        reloadAllReports();
      }
    });
  }
  return depot;
}

export class SASServer implements IServer {
  private _destroyed = false;
  @ltjsProperty
  private _server: Server | null = null;
  @ltjsProperty
  private _connection: Connection | null = null;
  private _url: string;
  private _authenticationType: AuthenticationType;
  private _errorNotifier = new ReplaySubject<string | undefined>(1);
  private _currentError: string | undefined;
  private _statusNotifier = new ReplaySubject<ConnectionStatus>(1);
  private _currentStatus: ConnectionStatus = 'initalizing';
  private _connected = false;
  private _authUnsubscribe: (() => void) | undefined;
  private _connectionAdded = false;
  private _initializedConnect = false;
  private _iframeSandbox: string | undefined;
  private _requestedSandboxSetting: boolean = false;
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  private _reportById: Map<string, SASReport> = new Map();

  constructor(opts: SASServerOptions) {
    this._url = opts.url;
    if (!validUrl(opts.url)) {
      this._setError(genericErrorMessage);
    }
    this._statusNotifier.next(this._currentStatus);
    this._authenticationType = opts.authenticationType;
    runWhenInitialized(() => {
      if (this._destroyed) {
        return;
      }
      this._server = createServer(opts.url);
    });

    combineLatest([locale$, formatterLocale$])
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
        skip(1),
        debounceTime(0, asyncScheduler)
      )
      .subscribe(() => {
        this.reloadAllReports();
      });
  }

  /**
   * Stream for notifying ConnectionStatus changes. Will replay the latest status when subscribed.
   */
  getStatusNotifier(): Observable<ConnectionStatus> {
    return this._statusNotifier;
  }

  /**
   * Stream for notifying changes to the error state of SASServer. Will replay the latest error state when subscribed.
   */
  getErrorNotifier(): Observable<string | undefined> {
    return this._errorNotifier;
  }

  getError() {
    return this._currentError;
  }

  getStatus() {
    return this._currentStatus;
  }

  getIframeSandbox() {
    return this._iframeSandbox;
  }

  /**
   * @internal
   */
  getConnection() {
    return this._connection;
  }

  /**
   * @internal
   */
  getConnectionManager() {
    return getDepot().getConnectionManager(this._connection);
  }

  private _setStatus(status: ConnectionStatus) {
    // We need to make sure both the sandbox and the connection is added before connected
    if (status === 'connected' && (!this._requestedSandboxSetting || !this._connectionAdded)) {
      return;
    }
    this._currentStatus = status;
    this._statusNotifier.next(status);
  }

  private _setError(error: string | undefined) {
    this._currentError = error;
    this._errorNotifier.next(error);
  }

  private _createConnectionManager() {
    if (!this._server) {
      this._setError(genericErrorMessage);
      throw new Error('Server connection closed.');
    }
    this._connection = createConnection(this._server);
    const currentManger = this.getConnectionManager();
    if (!currentManger) {
      this._setError(genericErrorMessage);
      throw new Error('Could not establish connection, getConnectionManager does not exist');
    }
    const configVoucher = currentManger.getServerConfig();
    if (!configVoucher) {
      this._setError(genericErrorMessage);
      throw new Error('Could not establish connection, serverConfig Voucher does not exist');
    }
    new TransportVoucher(configVoucher)
      .then(() => {
        if (this._currentError) {
          return;
        }
        this._performAuthorization();
      })
      .warning(() => {
        // Because CORS returns NO_RESPONSE response codes rather than 401s we always get "unexpected service error"
        this._setError(genericErrorMessage);
      });
  }

  getServerType(): ServerType {
    return 'viya';
  }

  private _performAuthorization() {
    if (!this._connection) {
      this._createConnectionManager();
      return;
    }
    const manager = this.getConnectionManager();
    if (!manager) {
      this._setError(genericErrorMessage);
      console.error('Could not establish connection, getConnectionManager does not exist');
      return;
    }

    // This is a hack to workaround potential authorization issues with cookie based authentication.
    //  If two authorization calls are made at the same time we get 401 POSSIBLE_CSRF errors. To
    //  avoid the problems associated with this error, we make these calls up front so that these
    //  service endpoint are authorized before portable starts making async calls to services.
    const url = this._url.endsWith('/') ? this._url : this._url + '/';
    Promise.allSettled([
      fetch(`${url}reportPackages/`, { credentials: 'include' }),
      fetch(`${url}folders/`, { credentials: 'include' }),
      fetch(`${url}reportData/`, { credentials: 'include' }),
      fetch(`${url}reports/`, { credentials: 'include' }),
    ]).finally(() => {
      const voucher = this.getConnectionManager()?.initializeSession('false');
      if (!voucher) {
        this._setError(genericErrorMessage);
        return;
      }
      if (this._connectionAdded) {
        this._setStatus('connected');
        return;
      }
      new TransportVoucher(voucher)
        .fulfilled(() => {
          this._connectionAdded = true;
          // @ts-ignore we might want to do this all the time, but limit to tests for now.
          if (sas.ltjs.jest) {
            this._connection?.setSessionToken(null);
          }
          const transportWarning = getDepot().addConnection(this._connection);
          if (transportWarning) {
            this._setError(processWarning(transportWarning));
            return;
          }
          this._setStatus('connected');
        })
        .warning((voucher: TransportVoucher) => {
          this._setError(processWarning(voucher.getWarning()));
        });
    });
  }

  connect() {
    if (this._connected) {
      return;
    }
    this._connected = true;

    this._authUnsubscribe = getConnection(this._url, this._authenticationType).subscribe((e) => {
      if (e.error) {
        this._setError(e.error);
      } else {
        this._setError(undefined);
        if (e.status !== 'authorized') {
          const oldStatus = this.getStatus();
          this._setStatus(e.status);
          if (oldStatus === 'connected') {
            this.deauthorize();
          }
        }
        if (e.status === 'authorized' && !this._initializedConnect) {
          // Load font definitions from the server
          registerServerFonts(this._url)
            .then(() => {
              return Promise.all([
                document.fonts.load('12px AvenirNext'),
                document.fonts.load('bold 12px AvenirNext'),
              ]);
            })
            .catch(() => {
              /* ignore errors */
            });

          this._setStatus('initalizing');
          this._iframeSandbox = undefined;
          fetch(`${this._url}/visualAnalyticsAdministration/commons/settings`, {
            credentials: 'include',
          })
            // Old servers (8.5 etc) have a different endpoint
            .catch(() =>
              fetch(`${this._url}/SASVisualAnalytics/commons/settings`, {
                credentials: 'include',
              })
            )
            .then((resp) => {
              if (!resp.ok) {
                return;
              }
              return resp.json();
            })
            .then((json) => {
              this._iframeSandbox = json?.attributes?.iframeSandbox;
            })
            .finally(() => {
              this._requestedSandboxSetting = true;
              this._setStatus('connected');
            });
          this._initializedConnect = true;
          if (!this._connection) {
            try {
              whenInitialized().then(() => {
                this._createConnectionManager();
              });
            } catch (err) {
              this._setError(genericErrorMessage);
            }
          } else {
            this._performAuthorization();
          }
        }
      }
    });
  }

  deauthorize() {
    this._initializedConnect = false;
    deauthorize(this.getUrl());
    this.reloadAllReports();
  }

  reloadAllReports() {
    for (const item of this._reportById.values()) {
      item.reload();
    }
  }

  /**
   * @internal
   */
  getReportManager() {
    if (this._currentError || this._currentStatus !== 'connected') {
      return null;
    }
    return getDepot().getReportManager(this._connection);
  }

  getReport(identifierOrLocation: string) {
    return this._reportById.get(identifierOrLocation);
  }

  subscribe(identifierOrLocation: string, reportContent?: string) {
    let report = this._reportById.get(identifierOrLocation);
    if (!report) {
      report = new SASReport(this, identifierOrLocation, reportContent);
      this._reportById.set(identifierOrLocation, report);
    }
    return report;
  }

  subscribeWithUserState(reportUriWithUserState: string, reportUri: string, userState: string) {
    let report = this._reportById.get(reportUriWithUserState);
    if (!report) {
      report = new SASReport(this, reportUri, undefined, userState);
      this._reportById.set(reportUriWithUserState, report);
    }
    return report;
  }

  unsubscribe(identifierOrLocation: string) {
    const report = this._reportById.get(identifierOrLocation);
    if (!report) {
      return;
    }
    report.close();
    this._reportById.delete(identifierOrLocation);
  }

  getUrl() {
    return this._url;
  }

  getFinishedURLString() {
    if (this._server) {
      const baseUrl = this._server.getBaseURL();
      if (baseUrl) {
        return baseUrl.getFinishedURLString();
      }
    }
    return null;
  }

  get isConnectionEstablished() {
    return this._currentStatus === 'connected';
  }

  @ltjsDestructor
  close() {
    if (this._authUnsubscribe) {
      this._authUnsubscribe();
      this._authUnsubscribe = undefined;
    }
    for (const report of this._reportById.values()) {
      report.close();
    }
    this._reportById.clear();
    deleteReportImageManager(this._url, this._authenticationType);
    this._destroyed = true;
    this._destroyed$.next(true);
  }
}

/**
 * For testing only!
 * This should only be called after all Servers are cleaned up; in fact, you should probably
 * be calling cleanupTransport from SASReportManager instead.
 * @private
 */
export async function _removeDepot() {
  if (!depot) return;

  depotSubscription?.unsubscribe();
  depot.removeAllReports();
  await _logoffAllConnectionManagers(depot);
  depot.release();
  depot = null;
}

/**
 * For testing only!
 */
async function _logoffAllConnectionManagers(depot: Depot) {
  const connections = depot.getAllConnections();
  if (!connections || !connections.length) return Promise.resolve();

  const promises: Promise<void>[] = [];
  for (const connection of connections) {
    if (!connection) continue;
    const manager = depot!.getConnectionManager(connection);
    const description = connection.getDescription();

    const logoffVoucher = manager && manager.logoff();
    if (!logoffVoucher) continue;

    promises.push(
      new Promise((resolve, _reject) => {
        new TransportVoucher(logoffVoucher)
          .warning((voucher) => {
            const warning = voucher.getWarning();
            console.warn(`Error logging off ${description}: ${processWarning(warning)}`);
            resolve();
          })
          .then(() => {
            resolve();
          });
      })
    );
  }

  return Promise.all(promises);
}
